import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, ArrowLeft, Zap, Crown, Star, BrainCircuit, Award } from 'lucide-react';
import { Button } from '@/components/ui/button';

export default function AIModelsPage() {
  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-3xl font-bold mb-4">AI Models</h1>
        <p className="text-lg text-gray-700 mb-6">
          AISEOBlogGenerator gives you access to the most powerful AI language models available. Different models offer different capabilities, and your subscription tier determines which models you can access.
        </p>
      </div>

      <div className="prose max-w-none">
        <h2>Understanding AI Models</h2>
        <p>
          Each AI model has different strengths, capabilities, and use cases. Choosing the right model for your content needs can significantly impact the quality of the generated blog posts.
        </p>
        
        <h3>Available AI Models</h3>
        <p>
          AISEOBlogGenerator offers access to models from both OpenAI and Anthropic, two leading providers of large language models.
        </p>
      </div>

      <div className="grid gap-6 md:grid-cols-2">
        <div className="bg-white border rounded-lg p-6 shadow-sm">
          <div className="flex items-center gap-3 mb-4">
            <div className="bg-green-100 p-2 rounded-full">
              <BrainCircuit className="w-6 h-6 text-green-600" />
            </div>
            <h3 className="text-xl font-bold">OpenAI Models</h3>
          </div>
          
          <div className="space-y-4">
            <div className="border rounded p-3">
              <div className="flex justify-between">
                <h4 className="font-semibold">GPT-4o</h4>
                <div className="flex gap-1">
                  <span className="inline-block text-xs py-0.5 px-1.5 rounded-full bg-gray-100 text-gray-800">Standard Performance</span>
                </div>
              </div>
              <p className="text-sm text-gray-600 mt-2">
                OpenAI's GPT-4o model offers strong general capabilities and is suitable for most content needs.
              </p>
              <div className="mt-2 text-xs text-gray-500">Available on: All plans</div>
            </div>
            
            <div className="border rounded p-3">
              <div className="flex justify-between">
                <h4 className="font-semibold">GPT-4o Mini</h4>
                <div className="flex gap-1">
                  <span className="inline-block text-xs py-0.5 px-1.5 rounded-full bg-gray-100 text-gray-800">Standard Performance</span>
                </div>
              </div>
              <p className="text-sm text-gray-600 mt-2">
                A lighter version of GPT-4o, offering good quality with faster generation times.
              </p>
              <div className="mt-2 text-xs text-gray-500">Available on: All plans</div>
            </div>
            
            <div className="border rounded p-3">
              <div className="flex justify-between">
                <h4 className="font-semibold">o3-mini</h4>
                <div className="flex gap-1">
                  <span className="inline-block text-xs py-0.5 px-1.5 rounded-full bg-blue-100 text-blue-800">Industry Leading</span>
                  <span className="inline-block text-xs py-0.5 px-1.5 rounded-full bg-green-100 text-green-800">High Performance</span>
                </div>
              </div>
              <p className="text-sm text-gray-600 mt-2">
                A high-performance model that balances quality and speed, ideal for most business content.
              </p>
              <div className="mt-2 text-xs text-gray-500">Available on: Pro and higher plans</div>
            </div>
            
            <div className="border rounded p-3">
              <div className="flex justify-between">
                <h4 className="font-semibold">o1</h4>
                <div className="flex gap-1">
                  <span className="inline-block text-xs py-0.5 px-1.5 rounded-full bg-blue-100 text-blue-800">Industry Leading</span>
                  <span className="inline-block text-xs py-0.5 px-1.5 rounded-full bg-green-100 text-green-800">High Performance</span>
                </div>
              </div>
              <p className="text-sm text-gray-600 mt-2">
                Premium model with advanced reasoning capabilities, ideal for complex topics.
              </p>
              <div className="mt-2 text-xs text-gray-500">Available on: Pro and higher plans</div>
            </div>
          </div>
        </div>
        
        <div className="bg-white border rounded-lg p-6 shadow-sm">
          <div className="flex items-center gap-3 mb-4">
            <div className="bg-purple-100 p-2 rounded-full">
              <BrainCircuit className="w-6 h-6 text-purple-600" />
            </div>
            <h3 className="text-xl font-bold">Anthropic Models</h3>
          </div>
          
          <div className="space-y-4">
            <div className="border rounded p-3">
              <div className="flex justify-between">
                <h4 className="font-semibold">Claude 4 Opus</h4>
                <div className="flex flex-wrap gap-1">
                  <span className="inline-block text-xs py-0.5 px-1.5 rounded-full bg-pink-100 text-pink-800">Latest</span>
                  <span className="inline-block text-xs py-0.5 px-1.5 rounded-full bg-blue-100 text-blue-800">Industry Leading</span>
                  <span className="inline-block text-xs py-0.5 px-1.5 rounded-full bg-purple-100 text-purple-800">Advanced Reasoning</span>
                </div>
              </div>
              <p className="text-sm text-gray-600 mt-2">
                Anthropic's most powerful model with exceptional writing quality, reasoning, and extended thinking capabilities.
              </p>
              <div className="mt-2 text-xs text-gray-500">Available on: All plans</div>
            </div>
            
            <div className="border rounded p-3">
              <div className="flex justify-between">
                <h4 className="font-semibold">Claude 4 Sonnet</h4>
                <div className="flex flex-wrap gap-1">
                  <span className="inline-block text-xs py-0.5 px-1.5 rounded-full bg-pink-100 text-pink-800">Latest</span>
                  <span className="inline-block text-xs py-0.5 px-1.5 rounded-full bg-blue-100 text-blue-800">Industry Leading</span>
                  <span className="inline-block text-xs py-0.5 px-1.5 rounded-full bg-purple-100 text-purple-800">Advanced Reasoning</span>
                </div>
              </div>
              <p className="text-sm text-gray-600 mt-2">
                Balanced Claude 4 model with excellent writing quality and reasoning capabilities at a faster speed.
              </p>
              <div className="mt-2 text-xs text-gray-500">Available on: All plans</div>
            </div>
            
            <div className="border rounded p-3">
              <div className="flex justify-between">
                <h4 className="font-semibold">Claude 3.7 Sonnet</h4>
                <div className="flex flex-wrap gap-1">
                  <span className="inline-block text-xs py-0.5 px-1.5 rounded-full bg-blue-100 text-blue-800">Industry Leading</span>
                  <span className="inline-block text-xs py-0.5 px-1.5 rounded-full bg-green-100 text-green-800">High Performance</span>
                  <span className="inline-block text-xs py-0.5 px-1.5 rounded-full bg-purple-100 text-purple-800">Advanced Reasoning</span>
                </div>
              </div>
              <p className="text-sm text-gray-600 mt-2">
                Anthropic's flagship model with excellent writing quality and reasoning capabilities.
              </p>
              <div className="mt-2 text-xs text-gray-500">Available on: Pro and higher plans</div>
            </div>
            
            <div className="border rounded p-3">
              <div className="flex justify-between">
                <h4 className="font-semibold">Claude 3.5 Sonnet</h4>
                <div className="flex gap-1">
                  <span className="inline-block text-xs py-0.5 px-1.5 rounded-full bg-green-100 text-green-800">High Performance</span>
                  <span className="inline-block text-xs py-0.5 px-1.5 rounded-full bg-orange-100 text-orange-800">Great Language Handling</span>
                </div>
              </div>
              <p className="text-sm text-gray-600 mt-2">
                Excellent for content requiring nuanced language and natural flow.
              </p>
              <div className="mt-2 text-xs text-gray-500">Available on: All plans</div>
            </div>
            
            <div className="border rounded p-3">
              <div className="flex justify-between">
                <h4 className="font-semibold">Claude 3 Haiku</h4>
                <div className="flex gap-1">
                  <span className="inline-block text-xs py-0.5 px-1.5 rounded-full bg-gray-100 text-gray-800">Standard Performance</span>
                </div>
              </div>
              <p className="text-sm text-gray-600 mt-2">
                A faster, lighter Claude model that's good for shorter content pieces.
              </p>
              <div className="mt-2 text-xs text-gray-500">Available on: All plans</div>
            </div>
            
            <div className="border rounded p-3">
              <div className="flex justify-between">
                <h4 className="font-semibold">Claude 3 Opus</h4>
                <div className="flex gap-1">
                  <span className="inline-block text-xs py-0.5 px-1.5 rounded-full bg-gray-100 text-gray-800">Standard Performance</span>
                </div>
              </div>
              <p className="text-sm text-gray-600 mt-2">
                A balanced model offering good quality for a variety of content tasks.
              </p>
              <div className="mt-2 text-xs text-gray-500">Available on: All plans</div>
            </div>
          </div>
        </div>
      </div>

      <div className="mt-8 bg-white border rounded-lg p-6">
        <h3 className="text-xl font-bold mb-4">Model Access by Subscription Tier</h3>
        <div className="overflow-x-auto">
          <table className="min-w-full border-collapse">
            <thead>
              <tr className="bg-gray-50">
                <th className="py-3 px-4 text-left border">Model</th>
                <th className="py-3 px-4 text-center border">
                  <div className="flex items-center justify-center gap-1">
                    <Star className="w-4 h-4 text-gray-400" />
                    <span>Free</span>
                  </div>
                </th>
                <th className="py-3 px-4 text-center border">
                  <div className="flex items-center justify-center gap-1">
                    <Zap className="w-4 h-4 text-blue-600" />
                    <span>Pro</span>
                    <span className="text-[10px] bg-blue-100 text-blue-800 px-1 rounded">$9.95</span>
                  </div>
                </th>
                <th className="py-3 px-4 text-center border">
                  <div className="flex items-center justify-center gap-1">
                    <Crown className="w-4 h-4 text-purple-600" />
                    <span>Enterprise</span>
                    <span className="text-[10px] bg-blue-100 text-blue-800 px-1 rounded">$49.95</span>
                  </div>
                </th>
                <th className="py-3 px-4 text-center border">
                  <div className="flex items-center justify-center gap-1">
                    <Award className="w-4 h-4 text-amber-600" />
                    <span>Enterprise VIP</span>
                  </div>
                </th>
              </tr>
            </thead>
            <tbody>
              <tr className="hover:bg-gray-50 transition-colors">
                <td className="py-2 px-4 border">GPT-4o</td>
                <td className="py-2 px-4 text-center border">✓</td>
                <td className="py-2 px-4 text-center border">✓</td>
                <td className="py-2 px-4 text-center border">✓</td>
                <td className="py-2 px-4 text-center border">✓</td>
              </tr>
              <tr className="hover:bg-gray-50 transition-colors">
                <td className="py-2 px-4 border">GPT-4o Mini</td>
                <td className="py-2 px-4 text-center border">✓</td>
                <td className="py-2 px-4 text-center border">✓</td>
                <td className="py-2 px-4 text-center border">✓</td>
                <td className="py-2 px-4 text-center border">✓</td>
              </tr>
              <tr className="hover:bg-gray-50 transition-colors">
                <td className="py-2 px-4 border">GPT-4.5 Preview</td>
                <td className="py-2 px-4 text-center border">✓</td>
                <td className="py-2 px-4 text-center border">✓</td>
                <td className="py-2 px-4 text-center border">-</td>
                <td className="py-2 px-4 text-center border">✓</td>
              </tr>
              <tr className="hover:bg-gray-50 transition-colors">
                <td className="py-2 px-4 border">o3-mini</td>
                <td className="py-2 px-4 text-center border">-</td>
                <td className="py-2 px-4 text-center border">✓</td>
                <td className="py-2 px-4 text-center border">✓</td>
                <td className="py-2 px-4 text-center border">✓</td>
              </tr>
              <tr className="hover:bg-gray-50 transition-colors">
                <td className="py-2 px-4 border">o1</td>
                <td className="py-2 px-4 text-center border">-</td>
                <td className="py-2 px-4 text-center border">✓</td>
                <td className="py-2 px-4 text-center border">✓</td>
                <td className="py-2 px-4 text-center border">✓</td>
              </tr>
              <tr className="hover:bg-gray-50 transition-colors">
                <td className="py-2 px-4 border">o1-mini</td>
                <td className="py-2 px-4 text-center border">-</td>
                <td className="py-2 px-4 text-center border">✓</td>
                <td className="py-2 px-4 text-center border">✓</td>
                <td className="py-2 px-4 text-center border">✓</td>
              </tr>
              <tr className="hover:bg-gray-50 transition-colors">
                <td className="py-2 px-4 border">Claude 4 Opus</td>
                <td className="py-2 px-4 text-center border">✓</td>
                <td className="py-2 px-4 text-center border">✓</td>
                <td className="py-2 px-4 text-center border">✓</td>
                <td className="py-2 px-4 text-center border">✓</td>
              </tr>
              <tr className="hover:bg-gray-50 transition-colors">
                <td className="py-2 px-4 border">Claude 4 Sonnet</td>
                <td className="py-2 px-4 text-center border">✓</td>
                <td className="py-2 px-4 text-center border">✓</td>
                <td className="py-2 px-4 text-center border">✓</td>
                <td className="py-2 px-4 text-center border">✓</td>
              </tr>
              <tr className="hover:bg-gray-50 transition-colors">
                <td className="py-2 px-4 border">Claude 3.7 Sonnet</td>
                <td className="py-2 px-4 text-center border">-</td>
                <td className="py-2 px-4 text-center border">✓</td>
                <td className="py-2 px-4 text-center border">✓</td>
                <td className="py-2 px-4 text-center border">✓</td>
              </tr>
              <tr className="hover:bg-gray-50 transition-colors">
                <td className="py-2 px-4 border">Claude 3.5 Sonnet</td>
                <td className="py-2 px-4 text-center border">✓</td>
                <td className="py-2 px-4 text-center border">✓</td>
                <td className="py-2 px-4 text-center border">✓</td>
                <td className="py-2 px-4 text-center border">✓</td>
              </tr>
              <tr className="hover:bg-gray-50 transition-colors">
                <td className="py-2 px-4 border">Claude 3 Haiku</td>
                <td className="py-2 px-4 text-center border">✓</td>
                <td className="py-2 px-4 text-center border">✓</td>
                <td className="py-2 px-4 text-center border">✓</td>
                <td className="py-2 px-4 text-center border">✓</td>
              </tr>
              <tr className="hover:bg-gray-50 transition-colors">
                <td className="py-2 px-4 border">Claude 3 Opus</td>
                <td className="py-2 px-4 text-center border">✓</td>
                <td className="py-2 px-4 text-center border">✓</td>
                <td className="py-2 px-4 text-center border">✓</td>
                <td className="py-2 px-4 text-center border">✓</td>
              </tr>
              <tr className="bg-gray-50">
                <td className="py-2 px-4 border font-medium">Monthly Price</td>
                <td className="py-2 px-4 text-center border">Free</td>
                <td className="py-2 px-4 text-center border">
                  <div>$9.95</div>
                  <div className="text-xs text-gray-500"><s>Regular: $24.95</s></div>
                </td>
                <td className="py-2 px-4 text-center border">
                  <div>$49.95</div>
                  <div className="text-xs text-gray-500"><s>Regular: $110</s></div>
                </td>
                <td className="py-2 px-4 text-center border">Contact Sales</td>
              </tr>
              <tr className="bg-gray-50">
                <td className="py-2 px-4 border font-medium">Annual Price (Save 20%)</td>
                <td className="py-2 px-4 text-center border">Free</td>
                <td className="py-2 px-4 text-center border">
                  <div>$79.95/year</div>
                  <div className="text-xs text-gray-500"><s>Regular: $249.50/year</s></div>
                </td>
                <td className="py-2 px-4 text-center border">
                  <div>$379/year</div>
                  <div className="text-xs text-gray-500"><s>Regular: $1,100/year</s></div>
                </td>
                <td className="py-2 px-4 text-center border">Contact Sales</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <div className="bg-gray-50 border rounded-lg p-6 mt-8">
        <h3 className="text-xl font-bold mb-4">Choosing the Right Model for Your Content</h3>
        <div className="space-y-4">
          <div className="bg-white p-4 rounded-md border">
            <h4 className="font-medium text-lg mb-2">General Content</h4>
            <p className="text-gray-600 mb-2">
              For standard blog posts on general topics, GPT-4o or Claude 3 Opus provide excellent results.
            </p>
            <span className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded-full">Recommended: GPT-4o</span>
          </div>
          
          <div className="bg-white p-4 rounded-md border">
            <h4 className="font-medium text-lg mb-2">Technical Content</h4>
            <p className="text-gray-600 mb-2">
              For complex technical topics that require precise explanations, o1 or Claude 4 Opus offers superior reasoning capabilities.
            </p>
            <span className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded-full">Recommended: Claude 4 Opus or o1</span>
          </div>
          
          <div className="bg-white p-4 rounded-md border">
            <h4 className="font-medium text-lg mb-2">Long-Form Content</h4>
            <p className="text-gray-600 mb-2">
              For in-depth, long-form content with nuanced explanations, Claude 4 models excel with their extended context windows.
            </p>
            <span className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded-full">Recommended: Claude 4 Sonnet or Claude 3.7 Sonnet</span>
          </div>
          
          <div className="bg-white p-4 rounded-md border">
            <h4 className="font-medium text-lg mb-2">Quick Drafts</h4>
            <p className="text-gray-600 mb-2">
              When you need quick content for drafting or ideation, lighter models provide faster results.
            </p>
            <span className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded-full">Recommended: Claude 3 Haiku or GPT-4o Mini</span>
          </div>
        </div>
      </div>

      <div className="bg-white border rounded-lg p-6 mt-8">
        <h3 className="text-xl font-bold mb-4">Extended Thinking Feature</h3>
        <p className="text-gray-700 mb-4">
          For Claude 3.7 Sonnet and Claude 4 models, we offer an "Extended Thinking" feature that allows the AI to spend more time analyzing and planning your content before generating it.
        </p>
        
        <div className="space-y-4">
          <div className="bg-blue-50 p-4 rounded-md border border-blue-100">
            <h4 className="font-medium text-lg mb-2">Benefits of Extended Thinking</h4>
            <ul className="list-disc pl-6 space-y-2 text-gray-700">
              <li>More coherent overall structure</li>
              <li>Better transitions between sections</li>
              <li>More thoughtful examples and evidence</li>
              <li>Improved keyword distribution for SEO</li>
              <li>More nuanced handling of complex topics</li>
            </ul>
          </div>
          
          <div className="bg-yellow-50 p-4 rounded-md border border-yellow-100">
            <h4 className="font-medium text-lg mb-2">When to Use Extended Thinking</h4>
            <p className="text-gray-700">
              Enable this feature when:
            </p>
            <ul className="list-disc pl-6 space-y-2 text-gray-700">
              <li>Creating in-depth, comprehensive content</li>
              <li>Covering complex or technical subjects</li>
              <li>Quality is more important than generation speed</li>
              <li>You need content with sophisticated reasoning</li>
            </ul>
            <p className="text-sm text-yellow-700 mt-3">
              <strong>Note:</strong> Extended thinking may increase generation time by 30-50%, but often results in significantly higher quality content.
            </p>
          </div>
        </div>
      </div>

      <div className="flex justify-between mt-8 pt-6 border-t">
        <Button variant="outline" asChild>
          <Link to="/docs/writer/writing-styles">
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back: Writing Styles
          </Link>
        </Button>
        <Button asChild>
          <Link to="/docs/posts">
            Next: Blog Posts
            <ArrowRight className="ml-2 h-4 w-4" />
          </Link>
        </Button>
      </div>
    </div>
  );
}